import type { MatchCandidateDto } from '../../api/catalogImportTypes'
import type { ProductTypeDto } from '../../api/productTypes'
import type { RestaurantPrivateSupplierDto } from '../../api/restaurantMyPrices'
import type { CellEditorGroup } from './CellEditor'

/** Ячейки «Моих прайсов», которые правятся кликом прямо в таблице; Tab идёт в этом порядке. */
export const MY_PRICES_EDITABLE_COLUMNS = ['name', 'supplier', 'price', 'unit', 'category'] as const
export type MyPricesEditableColumn = (typeof MY_PRICES_EDITABLE_COLUMNS)[number]

export const isEditableColumn = (columnId: string): columnId is MyPricesEditableColumn =>
  (MY_PRICES_EDITABLE_COLUMNS as readonly string[]).includes(columnId)

export type CellEditTarget = { itemId: number; column: MyPricesEditableColumn }

/** Следующая редактируемая ячейка той же строки среди видимых колонок (по кругу). */
export const neighbourColumn = (
  column: MyPricesEditableColumn,
  direction: 1 | -1,
  visibleColumnIds: readonly string[],
): MyPricesEditableColumn => {
  const order = visibleColumnIds.filter(isEditableColumn)
  const index = order.indexOf(column)
  if (index < 0 || order.length === 0) return column
  return order[(index + direction + order.length) % order.length]
}

const UNITS = ['кг', 'г', 'л', 'мл', 'шт', 'уп', 'кор', 'бут', 'бан', 'пак']

export const unitGroups = (): CellEditorGroup[] => [
  { options: UNITS.map((unit) => ({ value: unit, label: unit })) },
]

export const supplierGroups = (suppliers: RestaurantPrivateSupplierDto[], query: string): CellEditorGroup[] => {
  const needle = query.trim().toLowerCase()
  const options = suppliers
    .filter((supplier) => !needle || supplier.legalName.toLowerCase().includes(needle))
    .map((supplier) => ({ value: String(supplier.id), label: supplier.legalName }))
  return [{ options }]
}

const MAX_CATEGORY_OPTIONS = 8

/**
 * Категории для выпадающего списка. Над списком — «Возможно»: лучший кандидат матчера,
 * отличный от текущей категории (только для строк с точкой и пока ничего не напечатано).
 */
export const categoryGroups = (
  productTypes: ProductTypeDto[],
  query: string,
  currentTypeId: number | null,
  alternative: MatchCandidateDto | null,
): CellEditorGroup[] => {
  const needle = query.trim().toLowerCase().replace(/ё/g, 'е')
  const parents = new Map(productTypes.map((type) => [type.id, type.name]))
  const options = productTypes
    .filter((type) => type.active !== false)
    .filter((type) => !needle || type.name.toLowerCase().replace(/ё/g, 'е').includes(needle))
    .slice(0, MAX_CATEGORY_OPTIONS)
    .map((type) => ({
      value: String(type.id),
      label: type.name,
      meta: type.parentId != null ? (parents.get(type.parentId) ?? null) : null,
    }))
  if (!needle && alternative && alternative.productTypeId !== currentTypeId) {
    return [
      { label: 'Возможно', options: [{ value: String(alternative.productTypeId), label: alternative.name, hint: true }] },
      { label: 'Все категории', options },
    ]
  }
  return [{ options }]
}

/** «42,5», «1 720,00», «612» → число; мусор и отрицательные — null. */
export const parsePrice = (text: string): number | null => {
  const normalized = text.replace(/\s|\u00a0/g, '').replace('₽', '').replace(',', '.')
  if (!normalized) return null
  const value = Number(normalized)
  return Number.isFinite(value) && value >= 0 ? Math.round(value * 100) / 100 : null
}
